// Seated poses and the arm IK used for riding and paddling.
import * as THREE from 'three';
import * as SkeletonUtils from 'three/addons/utils/SkeletonUtils.js';
import { rr } from '../../lib/random.js';
import { V } from '../../world/sagrada.js';
import { state } from '../../core/state.js';
import { wq } from './retarget.js';
import { build, dress, fatten, outfit } from './outfits.js';

export let HQ, rotW, X, Z, Y, aim, ik, seat;

export function buildSeating() {
  seat = function seat(P, holder, k, hip, pose, grips, recolor = outfit()) {
    const model = dress(SkeletonUtils.clone(P.root), P, recolor);
    model.scale.setScalar(k);
    holder.add(model);
    model.userData.rider = true;
    holder.updateMatrixWorld(true);
    holder.getWorldQuaternion(HQ);
    model.traverse((o) => {
      if (o.isSkinnedMesh) {
        o.castShadow = true;
        o.receiveShadow = true;
        o.frustumCulled = false;
      }
    });
    // GLTFLoader strips dots from node names (UpperLeg.L -> UpperLegL)
    const b = (n) => model.getObjectByName(THREE.PropertyBinding.sanitizeNodeName(n));
    const ride = pose === 'ride',
      ground = pose === 'ground'; // 'ground': sitting on the sand, legs out in front, hands playing ahead
    // riders keep the full arm length: the grips are already at the edge of their reach
    if (ride) for (const s of ['L', 'R']) b('UpperArm.' + s).scale.setScalar(1);
    else fatten(model, build()[2]);
    for (const s of ['L', 'R']) {
      const sg = s === 'L' ? 1 : -1,
        spread = ride ? 0 : rr(0.04, 0.2);
      rotW(b('UpperLeg.' + s), X, ride ? -1.5 : -1.45);
      rotW(b('UpperLeg.' + s), Y, sg * spread);
      rotW(b('LowerLeg.' + s), X, ride ? 1.25 : ground ? rr(0.2, 0.7) : rr(1.3, 1.65));
      rotW(b('Foot.' + s), X, ride ? 0.15 : -0.1);
      // T-pose arms: bring them down to the sides, then forward (to the handlebar, or onto the thighs)
      rotW(b('UpperArm.' + s), Z, -sg * (ride ? 1.3 : ground ? 1.25 : 1.45));
      rotW(b('UpperArm.' + s), X, ride ? -0.75 : ground ? -0.9 : -0.2);
      rotW(b('LowerArm.' + s), X, ride ? -0.5 : -0.8);
      rotW(b('LowerArm.' + s), Z, sg * (ride ? 0.25 : -0.3));
    }
    rotW(b('Torso'), X, ride ? 0.3 : ground ? 0.35 : -0.04);
    if (ride) rotW(b('Chest'), X, 0.15);
    if (!ride) {
      rotW(b('Head'), Y, rr(-0.5, 0.5));
      rotW(b('Head'), X, rr(-0.05, 0.2));
    }
    holder.updateMatrixWorld(true);
    const h = holder.worldToLocal(b('Hips').getWorldPosition(new THREE.Vector3()));
    model.position.add(hip.clone().sub(h));
    holder.updateMatrixWorld(true);
    // hands on the grips (after seating, so the targets match): two-bone IK, elbows bending out and down
    if (grips)
      for (const s of ['L', 'R']) {
        const sg = s === 'L' ? 1 : -1;
        ik(
          b('UpperArm.' + s),
          b('LowerArm.' + s),
          b('Hand.' + s),
          holder.localToWorld(grips(sg)),
          V(sg, -0.7, -0.5).applyQuaternion(HQ),
        );
      }
    return { model, b };
  };
  // seated pose set directly on the bones (rotations about world axes, parents first)
  // rotate a bone about an axis given in the holder's frame (the bike), whatever the bike's current heading
  HQ = new THREE.Quaternion();
  rotW = (bone, axis, ang) => {
    bone.parent.updateMatrixWorld(true);
    const pq = bone.parent.getWorldQuaternion(new THREE.Quaternion()),
      wa = axis.clone().applyQuaternion(HQ);
    bone.quaternion.premultiply(
      pq.clone().invert().multiply(new THREE.Quaternion().setFromAxisAngle(wa, ang)).multiply(pq),
    );
    bone.updateMatrixWorld(true);
  };
  X = new THREE.Vector3(1, 0, 0);
  Z = new THREE.Vector3(0, 0, 1);
  Y = new THREE.Vector3(0, 1, 0);
  // Seat a character in `holder` with its hips at `hip` (holder-local). pose 'ride' reaches for handlebars, 'bench' rests hands on the lap.
  // turn a bone so the direction to its child points at world target t
  aim = (bone, child, t) => {
    bone.updateMatrixWorld(true);
    const p = bone.getWorldPosition(new THREE.Vector3());
    const q = new THREE.Quaternion().setFromUnitVectors(
      child.getWorldPosition(new THREE.Vector3()).sub(p).normalize(),
      t.clone().sub(p).normalize(),
    );
    const pq = wq(bone.parent);
    bone.quaternion.premultiply(pq.clone().invert().multiply(q).multiply(pq));
    bone.updateMatrixWorld(true);
  };
  ik = (up, lo, hand, T, pole) => {
    up.updateMatrixWorld(true);
    const S = up.getWorldPosition(new THREE.Vector3()),
      a = lo.getWorldPosition(new THREE.Vector3()).distanceTo(S),
      b2 = hand
        .getWorldPosition(new THREE.Vector3())
        .distanceTo(lo.getWorldPosition(new THREE.Vector3()));
    const d = Math.min(T.distanceTo(S), (a + b2) * 0.999),
      dir = T.clone().sub(S).normalize();
    const x = (a * a - b2 * b2 + d * d) / (2 * d),
      h = Math.sqrt(Math.max(0, a * a - x * x));
    const pl = pole.clone().addScaledVector(dir, -pole.dot(dir)).normalize();
    aim(up, lo, S.clone().addScaledVector(dir, x).addScaledVector(pl, h));
    aim(lo, hand, S.clone().addScaledVector(dir, d));
  };
  state.armIK = ik;
}
